import { useEffect, useRef } from 'react';

import { usePlayer } from '../../stores/player';
import { useRoomTuning } from '../../stores/room';
import { useSession } from '../../stores/session';
import { activity, type HeardContext } from './api';

/**
 * Telling the server what you are hearing.
 *
 * Once when a track starts, then every `BEAT` while it plays, and a delete
 * when playback stops for long enough that "now" would be a lie. The
 * server owns the switch: with sharing off it writes nothing and answers
 * `sharing: false`, and this backs off to one call per track rather than
 * one per beat, so a person who has said no is not costing a request a
 * minute to be told no again.
 *
 * ## Where it is heard from
 *
 * A room, when the player is tuned to one and playing what the room is
 * playing; a jam, when the queue is the jam's; a playlist or album, when
 * the play began from one (`origin` on the player); the track alone
 * otherwise. Decided here rather than sent from every play button, so
 * there is one answer.
 */
const BEAT = 60_000;
const STOP_AFTER = 3 * 60_000;

export function useListeningBeat() {
  const user = useSession((s) => s.user);
  const trackId = usePlayer((s) => s.track?.id ?? null);
  const playing = usePlayer((s) => s.playing);
  const source = usePlayer((s) => s.source);
  const origin = usePlayer((s) => s.origin);
  const tuned = useRoomTuning((s) => s.tuned);
  const playheadTrack = useRoomTuning((s) => s.playhead?.track_id ?? null);

  const sharing = useRef(true);
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user || trackId === null) return;

    if (!playing) {
      // Paused: the row stays for a while (a pause is not a stop), then goes.
      stopTimer.current = setTimeout(() => void activity.stopped().catch(() => {}), STOP_AFTER);

      return () => {
        if (stopTimer.current) clearTimeout(stopTimer.current);
      };
    }

    const context: HeardContext =
      tuned && playheadTrack === trackId
        ? { kind: 'room', username: tuned.username }
        : source === 'jam'
          ? { kind: 'jam' }
          : source === 'collection' && origin
            ? { kind: 'collection', id: origin.id, label: origin.title }
            : { kind: 'track' };

    const beat = () => {
      void activity
        .heard(trackId, context)
        .then((answer) => {
          sharing.current = answer.sharing;
        })
        .catch(() => {});
    };

    beat();

    // With sharing off, one call per track is enough to learn that; the
    // server clears the row on that call anyway.
    const timer = setInterval(() => {
      if (sharing.current) beat();
    }, BEAT);

    return () => clearInterval(timer);
  }, [user, trackId, playing, source, origin, tuned, playheadTrack]);

  // Sharing switched back on in settings: the next track will find out.
  useEffect(() => {
    sharing.current = user?.share_listening ?? true;
  }, [user?.share_listening]);
}
